import type { PlayerRanking } from '../types'
import { medalFor } from '../ranking'

import { FC } from 'react'
import styled from '@emotion/styled'
import Translate from 'intl/translate'
import { Colors } from 'common/presentation/colors'
import { styles, ui } from '@beelday/common'
import gold from './images/gold-medal.svg'
import silver from './images/silver-medal.svg'
import bronze from './images/bronze-medal.svg'

const ItemContainer = styled.div`
	border-top: solid 1px ${Colors.brightGray};
	display: flex;
	align-items: center;
	padding: 10px;
	height: 50px;
`

const Place = styled.div`
	background-color: ${Colors.paleGray};
	border-radius: 5px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	height: 31px;
	width: 31px;
	margin-right: 8px;
	color: ${Colors.indigoBlue};
	font-family: Ubuntu;
	font-size: 14px;
	font-weight: 500;

	${ui.responsive.smallTablet} {
		flex-shrink: 1;
		margin-right: 20px;
	}
`

const Name = styled.div`
	${styles.ellipsis};
	color: ${Colors.indigoBlue};
	font-family: Ubuntu;
	font-size: 14px;
	font-weight: 500;
	flex-grow: 1;
	flex-shrink: 2;
	min-width: 0;

	${ui.responsive.smallTablet} {
		min-width: auto;
	}
`

const ScoreContainer = styled.div`
	border-right: 1px solid ${Colors.brightGray};
	color: ${Colors.lightGray};
	font-family: Ubuntu;
	font-size: 12px;
	flex-shrink: 0;
	padding: 0 8px;
	white-space: nowrap;

	${ui.responsive.smallTablet} {
		flex-shrink: 1;
		padding: 0;
		white-space: normal;
		width: 100px;
	}
`

const AwardContainer = styled.div`
	color: ${Colors.lightGray};
	display: flex;
	align-items: center;
	font-family: Ubuntu;
	font-size: 12px;
	flex-shrink: 0;
	margin-left: 8px;
	white-space: nowrap;

	${ui.responsive.smallTablet} {
		flex-shrink: 1;
		margin-left: 15px;
		white-space: normal;
		width: 150px;
	}
`

const Medal = styled.img`
	height: 25px;
	width: 17px;
	margin-left: 10px;
`

type Props = {
	noWinners: boolean
	player: PlayerRanking & { displayName?: string }
	total: number
}

export const Score = ({ score, total }: { score: number; total: number }) => (
	<ScoreContainer>
		{`${score}/${total} `}
		<Translate>quiz.points</Translate>
	</ScoreContainer>
)

export const Award = (player: Pick<PlayerRanking, 'place' | 'score'>) => {
	switch (medalFor(player)) {
		case 1:
			return (
				<AwardContainer>
					<Translate>quiz.award_badge</Translate>
					<Medal src={gold} />
				</AwardContainer>
			)
		case 2:
			return (
				<AwardContainer>
					<Translate>quiz.award_badge</Translate>
					<Medal src={silver} />
				</AwardContainer>
			)
		case 3:
			return (
				<AwardContainer>
					<Translate>quiz.award_badge</Translate>
					<Medal src={bronze} />
				</AwardContainer>
			)
		default:
			return <AwardContainer></AwardContainer>
	}
}

const WinnersListItem: FC<Props> = ({ noWinners, player, total }) => {
	return noWinners ? (
		<ItemContainer>
			<Name>{player.displayName || player.name}</Name>
			<Score score={player.score} total={total} />
		</ItemContainer>
	) : (
		<ItemContainer>
			<Place>{player.place}</Place>
			<Name>{player.displayName || player.name}</Name>
			<Score score={player.score} total={total} />
			<Award place={player.place} score={player.score} />
		</ItemContainer>
	)
}

export default WinnersListItem
